import { Input, Component, OnDestroy, OnInit, Output, EventEmitter, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';

import { MoodEnum, FeedbackStandEndpointsService, DataService, GlobalVariables } from 'app/common';
import { TranslationKeys } from 'app/common/translations';
import { heightTransition, fadePanelTransition, springContentTransition } from 'app/common/common.animation';

declare var swal: any;

type RestoreStateResult = 'restored' | 'expired' | 'none';

@Component({
  selector: 'question-mood',
  templateUrl: './question-mood.component.html',
  styleUrls: ['./question-mood.component.scss'],
  providers: [FeedbackStandEndpointsService],
  animations: [heightTransition, fadePanelTransition, springContentTransition],
})
export class QuestionMoodComponent implements OnInit, OnDestroy {
  @Input() mood: any;
  @Input() questions: any[] = [];
  @Input() closeDelayMs = 1500;
  @Input() thankYouTemplate: TemplateRef<void> | null = null;
  @Output() completed = new EventEmitter<void>();
  @ViewChild('textarea') textAreaEl: ElementRef;

  formData: any = { answers_feedbacks_attributes: [] };
  currentQuestion: any = null;
  currentIndex = 0;
  formSubmitted = false;
  showTextArea = false;

  private inactiveTimeout: any = null;
  private completionTimeout: any = null;

  private readonly questionTimeoutMs = 10000;
  private readonly textAnswerTimeoutMs = 12000;
  private readonly sessionKeyPrefix = 'feedbackStand.questionSession.';

  constructor(
    private router: Router,
    private feedbackService: FeedbackStandEndpointsService,
    private dataService: DataService,
  ) {}

  get showCustomThankYou() {
    return this.formSubmitted && !!this.thankYouTemplate;
  }

  ngOnInit() {
    this.initializeQuestionnaire();
  }

  ngOnDestroy() {
    this.clearTimeouts();
  }

  initializeQuestionnaire() {
    this.formData = {
      answers_feedbacks_attributes: [],
      branch_id: this.dataService.branchData(),
      rating: this.setMoodRating(),
    };

    if (!this.questions || this.questions.length === 0) {
      this.submitForm();
      return;
    }

    const restoreState = this.restoreQuestionSession();
    if (restoreState === 'expired') {
      this.completed.emit();
      return;
    }

    if (restoreState !== 'restored') {
      this.currentIndex = 0;
      this.showTextArea = false;
    }

    this.currentQuestion = this.questions[this.currentIndex];
    this.persistQuestionSession();
    this.setInactiveTimeout(this.currentQuestion.id, this.getCurrentTimeoutMs());
  }

  answerSelected(answer: number | string) {
    const answerValue = String(answer);
    const feedbackData: any = {
      answer_id: answerValue,
      question_id: this.currentQuestion.id,
    };

    if (answerValue === '-1') {
      feedbackData.comment = this.getTextAreaValue();
    }

    this.formData.answers_feedbacks_attributes.push(feedbackData);
    this.showTextArea = false;
    this.currentIndex++;

    if (this.currentIndex >= this.questions.length) {
      this.submitForm();
      return;
    }

    this.currentQuestion = this.questions[this.currentIndex];
    this.persistQuestionSession();
    this.setInactiveTimeout(this.currentQuestion.id, this.questionTimeoutMs);
  }

  displayTextArea() {
    this.showTextArea = true;
    this.persistQuestionSession();

    setTimeout(() => {
      if (this.textAreaEl && this.textAreaEl.nativeElement) {
        this.textAreaEl.nativeElement.focus();
      }
    }, 150);

    this.setInactiveTimeout(this.currentQuestion.id, this.textAnswerTimeoutMs);
  }

  onTextareaChange() {
    this.persistQuestionSession();
    this.setInactiveTimeout(this.currentQuestion.id, this.textAnswerTimeoutMs);
  }

  setColor() {
    if (this.mood.type == MoodEnum['excellent']) {
      return 'excellent-button';
    } else if (this.mood.type == MoodEnum['good']) {
      return 'good-button';
    } else if (this.mood.type == MoodEnum['indifferent']) {
      return 'indifferent-button';
    } else if (this.mood.type == MoodEnum['bad']) {
      return 'bad-button';
    }

    return '';
  }

  setDynamicClass() {
    if (this.mood.type == MoodEnum['excellent']) {
      return 'row-excellent';
    } else if (this.mood.type == MoodEnum['good']) {
      return 'row-good';
    } else if (this.mood.type == MoodEnum['indifferent']) {
      return 'row-indifferent';
    } else if (this.mood.type == MoodEnum['bad']) {
      return 'row-bad';
    }

    return '';
  }

  moodLogo() {
    if (this.mood.type == MoodEnum['excellent']) {
      return '/assets/images/excellent-smiley.svg';
    } else if (this.mood.type == MoodEnum['good']) {
      return '/assets/images/good-smiley.svg';
    } else if (this.mood.type == MoodEnum['indifferent']) {
      return '/assets/images/neutral-smiley.svg';
    } else if (this.mood.type == MoodEnum['bad']) {
      return '/assets/images/bad-smiley.svg';
    }

    return '';
  }

  setMoodRating() {
    switch (this.mood.type) {
      case 0: {
        return 4;
      }
      case 1: {
        return 3;
      }
      case 2: {
        return 2;
      }
      case 3: {
        return 1;
      }
      default: {
        return 0;
      }
    }
  }

  getMoodTranslationKey(): keyof TranslationKeys {
    const map: Record<number, keyof TranslationKeys> = {
      [MoodEnum.excellent]: 'feedback_excellent',
      [MoodEnum.good]: 'feedback_good',
      [MoodEnum.indifferent]: 'feedback_indifferent',
      [MoodEnum.bad]: 'feedback_bad',
    };
    return map[this.mood?.type] ?? 'feedback_bad';
  }

  getProgressIndexes() {
    return Array(this.questions.length)
      .fill(0)
      .map((_, index) => index);
  }

  errorMessage(error: any) {
    if (error?.[0]?.status == '404') {
      return GlobalVariables.ERROR_MESSAGE_404;
    }

    if (error?.[0]?.message) {
      return error[0].message;
    }

    return GlobalVariables.DEFAULT_ERROR_MESSAGE;
  }

  submitForm() {
    if (this.formSubmitted) {
      return;
    }

    this.formSubmitted = true;
    this.showTextArea = false;
    this.clearQuestionSession();

    if (this.inactiveTimeout) {
      clearTimeout(this.inactiveTimeout);
      this.inactiveTimeout = null;
    }

    this.feedbackService.generateFeedback(this.formData).subscribe({
      next: () => {
        this.completionTimeout = setTimeout(() => {
          this.completed.emit();
        }, this.closeDelayMs);
      },
      error: (error) => {
        this.formSubmitted = false;
        this.persistQuestionSession();

        swal({
          text: this.errorMessage(error),
          type: 'warning',
          allowOutsideClick: false,
          showCancelButton: false,
          allowEscapeKey: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Refresh the page!',
        }).then(() => {
          if (error?.[0]?.status == '401') {
            this.dataService.clearData();
            this.router.navigate(['login']);
          } else {
            location.reload();
          }
        });
      },
    });
  }

  private restoreQuestionSession(): RestoreStateResult {
    const storedSession = window.localStorage.getItem(this.getSessionStorageKey());
    if (!storedSession) {
      return 'none';
    }

    try {
      const session = JSON.parse(storedSession);

      if (session.moodType !== this.mood.type) {
        this.clearQuestionSession();
        return 'none';
      }

      const timeoutWindow = session.showTextArea ? this.textAnswerTimeoutMs : this.questionTimeoutMs;
      if (Date.now() - session.lastInteraction > timeoutWindow) {
        this.clearQuestionSession();
        return 'expired';
      }

      if (session.index < 0 || session.index >= this.questions.length) {
        this.clearQuestionSession();
        return 'none';
      }

      this.currentIndex = session.index;
      this.showTextArea = !!session.showTextArea;
      return 'restored';
    } catch (error) {
      this.clearQuestionSession();
      return 'none';
    }
  }

  private persistQuestionSession() {
    window.localStorage.setItem(
      this.getSessionStorageKey(),
      JSON.stringify({
        moodType: this.mood.type,
        index: this.currentIndex,
        showTextArea: this.showTextArea,
        lastInteraction: Date.now(),
      }),
    );
  }

  private clearQuestionSession() {
    window.localStorage.removeItem(this.getSessionStorageKey());
  }

  private setInactiveTimeout(questionId: number, timeout: number) {
    if (this.inactiveTimeout) {
      clearTimeout(this.inactiveTimeout);
    }

    this.inactiveTimeout = setTimeout(() => {
      if (!this.currentQuestion || questionId !== this.currentQuestion.id) {
        return;
      }

      if (this.showTextArea) {
        this.formData.answers_feedbacks_attributes.push({
          answer_id: '-1',
          question_id: this.currentQuestion.id,
          comment: this.getTextAreaValue(),
        });
      }

      this.submitForm();
    }, timeout);
  }

  private getCurrentTimeoutMs() {
    return this.showTextArea ? this.textAnswerTimeoutMs : this.questionTimeoutMs;
  }

  private getTextAreaValue() {
    if (!this.textAreaEl || !this.textAreaEl.nativeElement) {
      return '';
    }

    return this.textAreaEl.nativeElement.value;
  }

  private clearTimeouts() {
    if (this.inactiveTimeout) {
      clearTimeout(this.inactiveTimeout);
      this.inactiveTimeout = null;
    }

    if (this.completionTimeout) {
      clearTimeout(this.completionTimeout);
      this.completionTimeout = null;
    }
  }

  private getSessionStorageKey() {
    return `${this.sessionKeyPrefix}${this.dataService.branchData()}.${this.mood.type}`;
  }
}
